import { string } from 'prop-types';
import { api } from './api';

export async function getNecs(payload?: string) {
  try {
    const { data } = await api.post(
      'database/nec__c/aggregate',
      payload
        ? [
            {
              $match: {
                $or: [
                  {
                    cd_alcc: {
                      $regex: payload,
                      $options: 'i',
                    },
                  },
                  {
                    nm_alcc: {
                      $regex: payload,
                      $options: 'i',
                    },
                  },
                  {
                    nm_uor_sltt: {
                      $regex: payload,
                      $options: 'i',
                    },
                  },
                ],
              },
            },
          ]
        : [],
    );

    return data;
  } catch (e) {
    return [];
  }
}

/**
 * Lista de demandantes agrupada da `nec__c`, usada pelo cadastro de Ação (`pages/Acao/Create`).
 * A Realização Destaque passou a usar `getUorDemandantes`.
 *
 * Traz o `cd_uor_sltt` junto com o nome. O `$first` é seguro porque na `nec__c`
 * cada `nm_uor_sltt` tem exatamente um `cd_uor_sltt` — verificado em 16/07/2026:
 * 235 nomes distintos, nenhum com mais de um código e nenhum sem código.
 */
export async function getDemandantes() {
  try {
    const { data } = await api.post('database/nec__c/aggregate', [
      {
        $group: {
          _id: '$nm_uor_sltt',
          cd_uor_sltt: { $first: '$cd_uor_sltt' },
        },
      },
      {
        $project: {
          _id: 0,
          nm_uor_sltt: '$_id',
          cd_uor_sltt: 1,
        },
      },
    ]);

    return data;
  } catch (e) {
    return [];
  }
}
/**
 * Diz se o UOR do jogador exige NEC/Iniciativa vinculada.
 *
 * Propaga o erro de propósito: o `catch` que devolvia `[]` tornava "a consulta
 * falhou" indistinguível de "este UOR não precisa de NEC", e o cadastro seguia
 * sem demandante. Quem chama precisa tratar os três estados — carregando, erro
 * e vazio de fato.
 */
export async function getUors(cod_urs?: number) {
  const payload = [
    {
      $match: {
        cd_uor: cod_urs?.toString(),
      },
    },
  ];
  const { data } = await api.post('database/uors__c/aggregate', payload);

  return data;
}

/** UOR como a tabela `uors__c` guarda: código, nome por extenso e, quando o BB envia, o nome reduzido. */
export type UorDemandante = {
  cd_uor: string;
  tx_nm_uor_cmt: string;
  tx_nm_uor_rdz?: string;
};

/**
 * Sem `Range` a API devolve só os 100 primeiros documentos, e o dropdown filtra no
 * navegador: o que não vier na carga não pode ser escolhido.
 */
const UOR_DEMANDANTES_RANGE = 'items=0-100000';

/**
 * UORs que o cadastro da Realização Destaque oferece como demandante.
 *
 * A lista vem da tabela de UORs (`uors__c`), não mais do agrupamento da `nec__c`.
 * A montagem das opções fica em `buildDemandanteOptions`.
 */
export async function getUorDemandantes(): Promise<UorDemandante[]> {
  try {
    const { data } = await api.post(
      'database/uors__c/aggregate',
      [
        {
          $project: {
            _id: 0,
            cd_uor: 1,
            tx_nm_uor_cmt: 1,
            tx_nm_uor_rdz: 1,
          },
        },
      ],
      { headers: { Range: UOR_DEMANDANTES_RANGE } },
    );

    return Array.isArray(data) ? data : [];
  } catch (e) {
    return [];
  }
}
